import React, { useMemo } from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from 'recharts'
import { AggregatedMetrics } from '../../hooks/useAnalytics'

interface ReferrerBreakdownProps {
  metrics: AggregatedMetrics
  title?: string
}

const COLORS = {
  search_result: '#5c4b99',
  city_page: '#7c6baf',
  region_page: '#9c8bc5',
  search_engine: '#16a34a',
  external: '#0284c7',
  direct: '#6b7280',
  prisberegner: '#d97706',
}

const LABELS: Record<string, string> = {
  search_result: 'Søgeresultater',
  city_page: 'Byoversigt',
  region_page: 'Region',
  search_engine: 'Søgemaskine',
  external: 'Ekstern',
  direct: 'Direkte',
  prisberegner: 'Prisberegner',
}

export default function ReferrerBreakdown({
  metrics,
  title = 'Trafikkilder',
}: ReferrerBreakdownProps) {
  const theme = useTheme()

  const data = useMemo(() => {
    const sources = [
      { name: 'search_result', value: metrics.views_from_search_result },
      { name: 'city_page', value: metrics.views_from_city_page },
      { name: 'region_page', value: metrics.views_from_region_page },
      { name: 'search_engine', value: metrics.views_from_search_engine },
      { name: 'external', value: metrics.views_from_external },
      { name: 'direct', value: metrics.views_from_direct },
      { name: 'prisberegner', value: metrics.views_from_prisberegner },
    ]
    
    return sources
      .filter((s) => s.value > 0)
      .sort((a, b) => b.value - a.value)
      .map((s) => ({
        ...s,
        label: LABELS[s.name],
        color: COLORS[s.name as keyof typeof COLORS],
      }))
  }, [metrics])

  const total = data.reduce((sum, item) => sum + item.value, 0)

  if (total === 0) {
    return (
      <Box
        sx={{
          p: 3,
          textAlign: 'center',
          backgroundColor: 'background.paper',
          borderRadius: 2,
          border: '1px dashed',
          borderColor: 'divider',
        }}
      >
        <Typography color="text.secondary">
          Ingen trafikdata tilgængelig
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      {title && (
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {title}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        <Box sx={{ width: { xs: '100%', md: '40%' }, height: 250 }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={2}
                dataKey="value"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number) => [
                  `${value.toLocaleString('da-DK')} (${((value / total) * 100).toFixed(1)}%)`,
                  'Visninger',
                ]}
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 8,
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </Box>
        <Box sx={{ flex: 1 }}>
          {data.map((item) => (
            <Box
              key={item.name}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:last-child': { borderBottom: 'none' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: item.color,
                  }}
                />
                <Typography variant="body2" fontWeight={500}>
                  {item.label}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {((item.value / total) * 100).toFixed(1)}%
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {item.value.toLocaleString('da-DK')}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
}
